import * as React from "react";
import { RouteComponentProps } from "react-router";
import { DisplayMedium } from "@conserveit/blocks";
import { i18n } from "../../../i18n";
import { InfoBar } from "../InfoBar";
import { ToolBar } from "../ToolBar";
import { PageHeader } from "../../Layout/Page";
import Loading from "../../Loading";
import { updateMonitoringUrlHistory } from "../../../redux/modules/urlHistory/actionCreators";
import { updateDaysRange } from "../../../redux/modules/_BETA/selections";
import { ExpandableGroup } from "../ExpandableGroup";
import { updateQueryString } from "../utils";
import { useCounts, useQueryState } from "../utils/hooks";
import { TimeRange, SearchCB } from "../utils/types";
import { TIME_RANGE_OPTIONS } from "../utils/options";
import { useActions as useAnalysisActions } from "../../../context/analysis";
import { useActions } from "../../../context/insights/useActions";
import { useInsights } from "../../../context/insights";
import { clearStorage } from "../../../utils/storage";
import { EXPANDING_KEY } from "../utils/constants";

interface Props {
  history: RouteComponentProps["history"];
  updateHistory: typeof updateMonitoringUrlHistory;
  updateTimeRange: typeof updateDaysRange;
  timeRange: Insite.AppState["betaSelections"]["daysRange"];
  showSnoozed: Insite.AppState["insights"]["showSnoozed"];
}

const onTimeRangeChange =
  (
    updateTimeRange: typeof updateDaysRange,
    history: RouteComponentProps["history"]
  ) =>
  (days: TimeRange) => {
    updateQueryString(`${days}`, history, "daysRange");
    updateTimeRange(days);
  };

export const Insights: React.FunctionComponent<Props> = ({
  history,
  timeRange,
  showSnoozed,
  updateHistory,
  updateTimeRange,
}) => {
  const { setViewMode } = useAnalysisActions();

  React.useEffect(() => {
    setViewMode("insights");
  }, []);

  const [state, dispatch] = useInsights();

  const actions = useActions();

  const { setQuery } = useQueryState(history, updateHistory);

  const counts = useCounts(
    state.groupBy,
    state.composedAndGroupedOccurrences,
    state.composedAndGroupedInsights,
    showSnoozed
  );

  const onSearch: SearchCB = React.useCallback(
    (value) => {
      dispatch({
        type: "SET_COMPOSED_FILTERED_OCCURRENCES",
        composedFilteredOccurrences: value.filtered,
        search: value.query,
      });

      setQuery(value.query);
    },
    [state.isProcessed]
  );

  const onExpandAll = React.useCallback(() => {
    clearStorage({ key: EXPANDING_KEY.insights });
    actions.toggleExpandAll();
  }, [actions.toggleExpandAll]);

  const timeRangeOption = React.useMemo(() => {
    return TIME_RANGE_OPTIONS.insights.find((o) => o.value === timeRange);
  }, [timeRange]);

  const finalGrouped = React.useMemo(
    () => ({
      composed: state.composedAndGroupedInsights,
      group: state.groupBy,
    }),
    [state.groupBy, state.composedAndGroupedInsights, state.isProcessed]
  );

  if (state.isLoading || !state.isProcessed) return <Loading />;

  return (
    <>
      <PageHeader>
        <DisplayMedium>
          <InfoBar
            insightsCount={counts.insightsCount}
            siteCount={counts.siteCount}
            equipCount={counts.equipCount}
            timeRange={timeRangeOption ? timeRangeOption.label(i18n) : ""}
          />
        </DisplayMedium>
      </PageHeader>

      <ToolBar
        searchData={state.composedOccurrences}
        snoozedCount={counts.snoozedCount}
        currentGroup={state.groupBy}
        currentSort={state.sortBy}
        timeRange={timeRange}
        expandAll={state.expandAll}
        onExpand={onExpandAll}
        onSearch={onSearch}
        searchSessionKey="insightsSearch"
        initialSearch={state.search}
        onTimeRangeChange={onTimeRangeChange(updateTimeRange, history)}
        onGroupChange={actions.setGroupBy}
        onSortChange={actions.setSortBy}
        exportRawData={
          counts.insightsCount > 0 ? finalGrouped.composed : undefined
        }
      />

      <ExpandableGroup
        finalGrouped={finalGrouped}
        showSnoozed={showSnoozed}
        expandAll={state.expandAll}
        filtered={state.composedFilteredOccurrences}
      />
    </>
  );
};
